import React, { useState, useEffect, useCallback } from 'react'
import { Search, UserX, UserCheck, Shield, Pencil } from 'lucide-react'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'
import { isSystemAdmin } from '../utils/roles.ts'
import { AGENCY_ROLES } from '../constants/roles.ts'
import { TableHeader, TableContainer } from '../components/Skeleton.tsx'
import { useColumnResize } from '../hooks/useColumnResize.ts'
import Pagination, { type PaginationInfo } from '../components/Pagination.tsx'
import ConfirmModal from '../components/ConfirmModal.tsx'

interface UserRow {
  id: string
  name: string | null
  email: string
  is_system_admin: number
  created_at: string
  companyAccessCount: number
  agency_users: Array<{
    role: string
    agencies: { id: string; name: string }
  }>
}

export default function AdminUsers() {
  const { user } = useAuth()
  const [users, setUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  })
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'active' | 'deactivated'>('active')
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const { widths, startResize } = useColumnResize('admin-users-col-widths')

  // Confirm modal
  const [confirmAction, setConfirmAction] = useState<{
    type: 'deactivate' | 'reactivate'
    userId: string
    userName: string
  } | null>(null)

  // Edit user modal
  const [editingUser, setEditingUser] = useState<UserRow | null>(null)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editSaving, setEditSaving] = useState(false)

  const openEditModal = (u: UserRow) => {
    setEditingUser(u)
    setEditName(u.name || '')
    setEditEmail(u.email)
  }

  const handleEditSave = async () => {
    if (!editingUser) return
    setEditSaving(true)
    try {
      await api.put(`/api/admin/users/${editingUser.id}`, {
        name: editName.trim() || null,
        email: editEmail.trim(),
      })
      setEditingUser(null)
      fetchUsers(pagination.page)
    } catch (err: any) {
      console.error('Failed to update user:', err)
    } finally {
      setEditSaving(false)
    }
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const fetchUsers = useCallback(
    async (page = 1) => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '25',
          status: tab === 'active' ? 'ACTIVE' : 'DEACTIVATED',
        })
        if (search) params.set('search', search)
        const res = await api.get(`/api/admin/users/all?${params}`)
        setUsers(res.data.users || [])
        setPagination(res.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 })
      } catch (err) {
        console.error('Failed to fetch users:', err)
      } finally {
        setLoading(false)
      }
    },
    [search, tab],
  )

  useEffect(() => {
    fetchUsers(1)
  }, [fetchUsers])

  const handleAction = async () => {
    if (!confirmAction) return
    try {
      if (confirmAction.type === 'deactivate') {
        await api.put(`/api/admin/users/${confirmAction.userId}/deactivate`)
      } else if (confirmAction.type === 'reactivate') {
        await api.put(`/api/admin/users/${confirmAction.userId}/reactivate`)
      }
      setConfirmAction(null)
      fetchUsers(pagination.page)
    } catch (err: any) {
      console.error('Action failed:', err)
    }
  }

  if (!isSystemAdmin(user)) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">System admin access required.</p>
      </div>
    )
  }

  const sortedUsers = [...users].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    let av: string
    let bv: string
    switch (sortBy) {
      case 'email':
        av = a.email
        bv = b.email
        break
      case 'created_at':
        av = a.created_at
        bv = b.created_at
        break
      case 'role':
        av = a.is_system_admin
          ? 'A'
          : a.agency_users.some(au => au.role === AGENCY_ROLES.ADMIN)
            ? 'B'
            : 'C'
        bv = b.is_system_admin
          ? 'A'
          : b.agency_users.some(au => au.role === AGENCY_ROLES.ADMIN)
            ? 'B'
            : 'C'
        break
      case 'agency':
        av = a.agency_users.map(au => au.agencies.name).join(', ') || 'zzz'
        bv = b.agency_users.map(au => au.agencies.name).join(', ') || 'zzz'
        break
      case 'companies':
        return (a.companyAccessCount - b.companyAccessCount) * dir
      default:
        av = a.name || ''
        bv = b.name || ''
    }
    return av.localeCompare(bv) * dir
  })

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all platform users</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-md p-0.5 w-fit mb-6">
        <button
          onClick={() => setTab('active')}
          className={`px-3 py-1.5 text-xs font-medium rounded ${
            tab === 'active'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setTab('deactivated')}
          className={`px-3 py-1.5 text-xs font-medium rounded ${
            tab === 'deactivated'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Deactivated
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Table */}
      <TableContainer
        loading={loading}
        hasData={users.length > 0}
        skeletonRows={8}
        skeletonCols={6}
        emptyMessage={search ? 'No users match your search' : 'No users found'}
      >
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table
            className={`min-w-full divide-y divide-gray-200 ${Object.keys(widths).length > 0 ? 'table-fixed' : ''}`}
          >
            <TableHeader
              columns={[
                { label: 'Name', key: 'name' },
                { label: 'Email', key: 'email' },
                { label: 'Role', key: 'role' },
                { label: 'Agency', key: 'agency' },
                { label: 'Companies', key: 'companies' },
                { label: 'Joined', key: 'created_at' },
                {
                  label: 'Actions',
                  key: 'actions',
                  sortable: false,
                  resizable: false,
                  align: 'right',
                },
              ]}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={toggleSort}
              widths={widths}
              onResize={startResize}
            />
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedUsers.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {u.name || '—'}
                      {u.is_system_admin === 1 && (
                        <span title="System Admin">
                          <Shield size={14} className="text-blue-600" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                  <td className="px-6 py-4">
                    {u.is_system_admin === 1 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        System Admin
                      </span>
                    ) : u.agency_users.some(au => au.role === AGENCY_ROLES.ADMIN) ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        Agency Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Member
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {u.agency_users.map(au => au.agencies.name).join(', ') || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 text-center">
                    {u.companyAccessCount}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(u.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {u.is_system_admin !== 1 && u.id !== user?.id && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(u)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Edit user"
                        >
                          <Pencil size={16} />
                        </button>
                        {tab === 'active' ? (
                          <button
                            onClick={() =>
                              setConfirmAction({
                                type: 'deactivate',
                                userId: u.id,
                                userName: u.name || u.email,
                              })
                            }
                            className="text-orange-600 hover:text-orange-800"
                            title="Deactivate user"
                          >
                            <UserX size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              setConfirmAction({
                                type: 'reactivate',
                                userId: u.id,
                                userName: u.name || u.email,
                              })
                            }
                            className="text-green-600 hover:text-green-800"
                            title="Reactivate user"
                          >
                            <UserCheck size={16} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={p => fetchUsers(p)}
            className="mt-4"
          />
        )}
      </TableContainer>

      {/* Confirm Modal */}
      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.type === 'deactivate' ? 'Deactivate User' : 'Reactivate User'}
        message={
          confirmAction?.type === 'deactivate'
            ? `Deactivate "${confirmAction?.userName}"? They will no longer be able to log in.`
            : `Reactivate "${confirmAction?.userName}"? They will be able to log in again.`
        }
        confirmLabel={confirmAction?.type === 'deactivate' ? 'Deactivate' : 'Reactivate'}
        confirmVariant={confirmAction?.type === 'reactivate' ? 'primary' : 'danger'}
        onConfirm={handleAction}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditingUser(null)} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="User name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving || !editEmail.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {editSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
